import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    AppBar,
    Toolbar,
    Button,
    Typography,
    Box,
    Menu,
    MenuItem,
    Container,
    IconButton, // 引入 IconButton
    Slide // 引入 Slide 组件，用于动画效果 (可选)
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu'; // 引入 MenuIcon 图标
import AccountCircle from '@mui/icons-material/AccountCircle';
import { useTheme } from '@mui/material/styles';

const Navigation = () => {
    const navigate = useNavigate();
    const theme = useTheme(); 

    const [anchorElUser, setAnchorElUser] = React.useState(null);
    const [anchorElMobileMenu, setAnchorElMobileMenu] = React.useState(null);
    const [isLoggedIn, setIsLoggedIn] = React.useState(!!localStorage.getItem('token'));
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false); // 控制移动端菜单展开/收缩
    const [username, setUsername] = React.useState(localStorage.getItem('username') || ''); // 获取用户名

    // 用户菜单
    const handleUserMenuOpen = (event) => {
        setAnchorElUser(event.currentTarget);
    };
    const handleUserMenuClose = () => {
        setAnchorElUser(null);
    };

    // 移动端菜单
    const handleMobileMenuOpen = (event) => {
        setAnchorElMobileMenu(event.currentTarget);
        setIsMobileMenuOpen(true);
    };
    const handleMobileMenuClose = () => {
        setAnchorElMobileMenu(null);
        setIsMobileMenuOpen(false);
    };

    const handleLogout = async () => {
        try {
            await axios.get('/auth/logout');
            localStorage.removeItem('token');
            localStorage.removeItem('username'); //  新增：移除 localStorage 中的 username
            alert('登出成功！');
            navigate('/');
        } catch (error) {
            console.error('登出失败:', error.response?.data?.message || '登出失败');
            alert('登出失败: ' + (error.response?.data?.message || '请稍后重试'));
        }
        handleUserMenuClose();
        handleMobileMenuClose();
    };

    React.useEffect(() => {
        //  监听 localStorage username 的变化 (可选，如果需要实时更新)
        const handleStorageChange = () => {
            setUsername(localStorage.getItem('username') || 'Guest');
        };
        const checkAuth = () => {
            setIsLoggedIn(!!localStorage.getItem('token'));
            setUsername(localStorage.getItem('username') || ''); // 监听 localStorage 变化更新用户名
            console.log("useEffect - localStorage username:", localStorage.getItem('username')); // Log value directly from localStorage
            console.log("useEffect - state username:", username); // Log state value (might be outdated in this log)
        };
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('storage', checkAuth);
        checkAuth(); // 立即执行一次，获取初始状态
        return () => window.removeEventListener('storage', checkAuth);
        //return () => window.removeEventListener('storage', checkAuth);
    }, []);

    const navItems = ['solutions', 'requirements','subscription', 'payment', 'profile', 'subscription-management'];

    return (
        <>
            {/* 使用 fixed 定位，并根据 isMobileMenuOpen 条件渲染 AppBar 内容 */}
            <AppBar
                position="fixed" // 使用 fixed 定位
                color="default"
                elevation={1}
                sx={{
                    top: 0, // 始终固定在顶部
                    left: 0,
                    right: 0,
                    zIndex: 1100, // 确保在内容之上
                    // 根据 isMobileMenuOpen 状态控制 AppBar 的高度，实现收缩效果
                    height: isMobileMenuOpen ? 'auto' : '56px', // 展开时自动高度，收缩时固定高度 (例如 56px)
                    overflow: 'hidden', // 确保收缩时内容不溢出
                    transition: 'height 0.3s ease-in-out', // 添加高度变化的过渡动画 (可选)
                }}
            >
                {/* 条件渲染 Toolbar 和 Container 内容，只在 isMobileMenuOpen 为 true 时渲染 */}
                {isMobileMenuOpen ? (
                    <Container maxWidth="md">
                        <Toolbar disableGutters>
                            <Typography
                                variant="h6"
                                component={Link}
                                to="/"
                                sx={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    textDecoration: 'none',
                                    color: 'inherit'
                                }}
                            >
                                责联网
                            </Typography>

                            {/* 桌面导航栏 - 保持不变，但现在只在移动端菜单展开时显示 */}
                            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
                                {/* ... (桌面端导航按钮和账户/登录/注册) ... */}
                                {navItems.map((path) => (
                                    <Button
                                        key={path}
                                        color="inherit"
                                        component={Link}
                                        to={`/${path}`}
                                    >
                                        {{
                                            solutions: '解决方案',
                                            requirements: '新需求', // Added '新需求' for requirements path
                                            subscription: '订阅套餐',
                                            payment: '付费',
                                            profile: '个人资料',
                                            'subscription-management': '订阅管理'
                                        }[path]}
                                    </Button>
                                ))}

                                {/* 用户名显示 (桌面端) */}
                                {isLoggedIn && username && (
                                    <Typography variant="subtitle1" color="inherit" sx={{ mr: 2 }}>
                                        你好, {username}
                                    </Typography>
                                )}

                                {/* Google Translate 容器 (桌面端) */}
                                <Box id="google_translate_element" sx={{ ml: 2 }} />

                                {isLoggedIn ? (
                                    <div>
                                        <Button
                                            color="inherit"
                                            onClick={handleUserMenuOpen}
                                            endIcon={<AccountCircle />}
                                        >
                                            账户
                                        </Button>
                                        <Menu
                                            anchorEl={anchorElUser}
                                            open={Boolean(anchorElUser)}
                                            onClose={handleUserMenuClose}
                                        >
                                            <MenuItem onClick={handleLogout}>登出</MenuItem>
                                        </Menu>
                                    </div>
                                ) : (
                                    <>
                                        <Button color="inherit" component={Link} to="/login">登录</Button>
                                        <Button color="inherit" component={Link} to="/register">注册</Button>
                                    </>
                                )}
                            </Box>

                            {/* 移动端导航栏 - 汉堡菜单图标，始终显示 */}
                            <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-end' }}>
                                {/* 用户名显示 (移动端 - Toolbar 中，汉堡菜单图标左侧) */}
                                {isLoggedIn && username && (
                                    <Typography variant="subtitle1" color="inherit" sx={{ mr: 1 }}>
                                        {username}
                                    </Typography>
                                )}
                                <IconButton
                                    size="large"
                                    aria-label="menu"
                                    aria-controls="menu-appbar"
                                    aria-haspopup="true"
                                    onClick={handleMobileMenuClose} // 注意这里是 handleMobileMenuClose，用于收缩菜单
                                    color="inherit"
                                >
                                    <MenuIcon />
                                </IconButton>
                                {/* 移动端 Menu 组件 -  保持不变 */}
                                <Menu
                                    id="menu-appbar"
                                    anchorEl={anchorElMobileMenu}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                    keepMounted
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left',
                                    }}
                                    open={Boolean(anchorElMobileMenu)}
                                    onClose={handleMobileMenuClose}
                                    sx={{
                                        display: { xs: 'block', md: 'none' },
                                    }}
                                    
                                >
                                    {/* ... (移动端 MenuItems) ... */}
                                    {navItems.map((path) => (
                                        <MenuItem key={path} onClick={handleMobileMenuClose} component={Link} to={`/${path}`}>
                                            {{
                                                solutions: '解决方案',
                                                requirements: '新需求', // Added '新需求' for requirements path
                                                subscription: '订阅套餐',
                                                payment: '付费',
                                                profile: '个人资料',
                                                'subscription-management': '订阅管理'
                                            }[path]}
                                        </MenuItem>
                                    ))}
                                    <MenuItem>
                                        <Box id="google_translate_element" />
                                    </MenuItem>

                                    {isLoggedIn ? (
                                        <MenuItem onClick={handleLogout}>登出</MenuItem>
                                    ) : (
                                        <>
                                            <MenuItem onClick={handleMobileMenuClose} component={Link} to="/login">登录</MenuItem>
                                            <MenuItem onClick={handleMobileMenuClose} component={Link} to="/register">注册</MenuItem>
                                        </>
                                    )}
                                </Menu>
                            </Box>
                        </Toolbar>
                    </Container>
                ) : (
                    // 收缩状态下，只渲染 Toolbar，内部只放汉堡菜单图标
                    <Toolbar disableGutters sx={{ minHeight: '56px' }}>
                        <Typography
                            variant="h6"
                            component={Link}
                            to="/"
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                alignItems: 'center',
                                textDecoration: 'none',
                                color: 'inherit'
                            }}
                        >
                            责联网
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {/* 用户名显示 (收缩状态) */}
                            {isLoggedIn && username && (
                                <Typography variant="subtitle2" color="inherit">
                                    {username}
                                </Typography>
                            )}
                            {/* Google Translate 容器 (收缩状态) */}
                            <Box id="google_translate_element" />
                            <IconButton
                                size="large"
                                aria-label="menu"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleMobileMenuOpen} // 注意这里是 handleMobileMenuOpen，用于展开菜单
                                color="inherit"
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    </Toolbar>
                )}
            </AppBar>

            {/*  添加内容区域的 padding-top，防止被 fixed AppBar 遮挡 */}
            <Box sx={{ paddingTop: isMobileMenuOpen ? 'auto' : '56px' }}>
                {/*  你的页面主要内容放在这里  */}
                {/*  例如： <Routes> ... </Routes> */}
                {/* <Typography paragraph>
                    这里是页面主要内容区域，内容会根据导航栏的展开状态调整位置，避免被遮挡。
                </Typography>
                <Typography paragraph>
                    更多内容...
                </Typography> */}
            </Box>
        </>
    );
};

export default Navigation;